import { useQuery } from '@apollo/client/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { isNotFoundGraphQLError } from '../../lib/is-not-found-graphql-error.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  type LedgerFilter,
  NO_LEDGER_FILTER,
  useLotMovements,
} from '../../modules/lot-history/ledger/index.js';
import { lotDisplayName } from '../../modules/lot-history/lot-display-name.js';
import { LotHistoryPage } from '../../modules/lot-history/lot-history-page.js';
import { LotSearch } from '../../modules/lot-history/lot-search.js';
import { lotTrailEntry } from '../../modules/lot-history/lot-trail-entry.js';
import {
  GET_LOT_HISTORY,
  type GetLotHistoryQueryResult,
  type GetLotHistoryQueryVariables,
  type LotHistorySubject,
} from '../../modules/lot-history/operations.js';
import { parseLotHistoryRouteSearch } from '../../modules/lot-history/route-search.js';
import { useLotTrail } from '../../modules/lot-history/use-lot-trail.js';

export const Route = createFileRoute('/inventory/lot-history')({
  validateSearch: parseLotHistoryRouteSearch,
  component: LotHistoryRoute,
});

type LastGoodLot = { address: string; lot: LotHistorySubject };

// eslint-disable-next-line react-refresh/only-export-components -- route files export both Route and their page component; fast refresh flags the second export
function LotHistoryRoute() {
  const { partId, lotNumber, tagId } = Route.useSearch();
  const { permissions } = usePermissions();
  const canViewCost = permissions.includes('inventory:showpartcost');
  const canViewWorkOrders = permissions.includes('manufactureOrder:view');
  const canViewUsers = permissions.includes('user:view');
  const canViewHolds = permissions.includes('cannabisCompliance:view');
  const canViewQuality = canViewHolds || permissions.includes('strain:view');
  const address = JSON.stringify({ partId, lotNumber, tagId });
  const [chosen, setChosen] = useState<{ address: string; filter: LedgerFilter }>({
    address,
    filter: NO_LEDGER_FILTER,
  });
  if (chosen.address !== address) {
    setChosen({ address, filter: NO_LEDGER_FILTER });
  }
  const filter = chosen.address === address ? chosen.filter : NO_LEDGER_FILTER;
  const setFilter = (next: LedgerFilter) => setChosen({ address, filter: next });
  const skip = !(partId && lotNumber) && !tagId;
  const { data, loading, error, refetch } = useQuery<
    GetLotHistoryQueryResult,
    GetLotHistoryQueryVariables
  >(GET_LOT_HISTORY, {
    variables: {
      partId,
      lotNumber,
      tagId,
      canViewCost,
      canViewQuality,
      canViewHolds,
      canViewWorkOrders,
    },
    skip,
  });
  const movements = useLotMovements({
    address: { partId, lotNumber, tagId },
    filter,
    canViewCost,
    canViewWorkOrders,
    canViewUsers,
    skip,
  });

  // A failed refetch clears Apollo's `data`; fall back to the last good result for the SAME address only.
  const [lastGood, setLastGood] = useState<LastGoodLot | null>(null);
  if (data?.lotHistory && (lastGood?.lot !== data.lotHistory || lastGood.address !== address)) {
    setLastGood({ address, lot: data.lotHistory });
  }
  const served: LastGoodLot | null = data?.lotHistory
    ? { address, lot: data.lotHistory }
    : lastGood?.address === address
      ? lastGood
      : null;
  const lot = served?.lot ?? null;
  const { path: trail, setPath: setTrail } = useLotTrail(lot ? lotTrailEntry(lot) : null);
  const loadedLotNumber = data?.lotHistory?.lotNumber;
  useBreadcrumbLabel('lot-history', loadedLotNumber ? lotDisplayName(loadedLotNumber) : undefined);

  return (
    <LotHistoryPage
      lot={lot}
      loading={loading}
      error={isNotFoundGraphQLError(error, 'lotHistory') ? null : error}
      canViewCost={canViewCost}
      canViewWorkOrders={canViewWorkOrders}
      canViewUsers={canViewUsers}
      filter={filter}
      onFilterChange={setFilter}
      movements={movements}
      onRetry={() => void refetch()}
      trail={trail}
      onTrailChange={setTrail}
      searchSlot={
        <LotSearch currentPartId={lot?.partId ?? null} currentLotNumber={lot?.lotNumber ?? null} />
      }
    />
  );
}
